/**
 * ShutterKART API client.
 * Always reads REACT_APP_BACKEND_URL from env; never hardcoded.
 * Always prefixes /api on every call.
 *
 * Buyer auth tokens (Phase 3):
 *   localStorage `shutterkart_buyer_access_token` — short-lived JWT (1h)
 *   localStorage `shutterkart_buyer_refresh_token` — opaque 32-byte random
 *   localStorage `shutterkart_buyer` — {id, phone}
 *
 * Interceptor behaviour:
 *   - request: attach Authorization Bearer if access token present
 *   - response 401: try one /auth/refresh; if it succeeds, retry the original
 *     request. If it fails, clear tokens. Pages reading them re-render.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.warn("REACT_APP_BACKEND_URL is not set");
}

export const API_BASE = `${BACKEND_URL}/api`;
export const BACKEND_BASE = BACKEND_URL || "";

export const TOKEN_KEYS = {
  access: "shutterkart_buyer_access_token",
  refresh: "shutterkart_buyer_refresh_token",
  buyer: "shutterkart_buyer",
};

function readLS(key) {
  try {
    return typeof window === "undefined" ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeLS(key, val) {
  try {
    if (val == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, val);
  } catch {
    /* ignore */
  }
}

export function getStoredAccessToken() {
  return readLS(TOKEN_KEYS.access);
}
export function getStoredRefreshToken() {
  return readLS(TOKEN_KEYS.refresh);
}
export function getStoredBuyer() {
  const raw = readLS(TOKEN_KEYS.buyer);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
export function persistAuth({ access_token, refresh_token, buyer }) {
  if (access_token) writeLS(TOKEN_KEYS.access, access_token);
  if (refresh_token) writeLS(TOKEN_KEYS.refresh, refresh_token);
  if (buyer) writeLS(TOKEN_KEYS.buyer, JSON.stringify(buyer));
  // Broadcast for context providers
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("shutterkart-auth-change"));
  }
}
export function clearAuth() {
  writeLS(TOKEN_KEYS.access, null);
  writeLS(TOKEN_KEYS.refresh, null);
  writeLS(TOKEN_KEYS.buyer, null);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("shutterkart-auth-change"));
  }
}

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
});

// ----- Request interceptor: attach Bearer -----
api.interceptors.request.use((config) => {
  const access = getStoredAccessToken();
  if (access && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

// ----- Response interceptor: 401 → refresh once → retry -----
let refreshInFlight = null;
async function tryRefresh() {
  if (refreshInFlight) return refreshInFlight;
  const refresh = getStoredRefreshToken();
  if (!refresh) return null;
  refreshInFlight = axios
    .post(
      `${API_BASE}/auth/refresh`,
      { refresh_token: refresh },
      { timeout: 15000 },
    )
    .then((r) => {
      persistAuth(r.data);
      return r.data.access_token;
    })
    .catch(() => {
      clearAuth();
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (
      status === 401 &&
      !original._retried &&
      !String(original.url || "").includes("/auth/")
    ) {
      original._retried = true;
      const newAccess = await tryRefresh();
      if (newAccess) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      }
    }
    return Promise.reject(error);
  },
);

// Build an absolute URL for a backend-relative `url` (e.g. uploaded file).
export function absoluteUrl(relativeOrAbsolute) {
  if (!relativeOrAbsolute) return "";
  if (/^https?:\/\//i.test(relativeOrAbsolute)) return relativeOrAbsolute;
  if (relativeOrAbsolute.startsWith("/api/")) {
    return `${BACKEND_BASE}${relativeOrAbsolute}`;
  }
  if (relativeOrAbsolute.startsWith("/")) {
    return `${BACKEND_BASE}${relativeOrAbsolute}`;
  }
  return `${BACKEND_BASE}/${relativeOrAbsolute}`;
}

// ----- Phase 0 catalog reads -----
export const getHealth = () => api.get("/health").then((r) => r.data);
export const listServices = () => api.get("/services").then((r) => r.data);
export const getService = (slugOrId) =>
  api.get(`/services/${slugOrId}`).then((r) => r.data);
export const listPackages = (params = {}) =>
  api.get("/packages", { params }).then((r) => r.data);
export const getPackage = (id) =>
  api.get(`/packages/${id}`).then((r) => r.data);
export const listAddons = () => api.get("/addons").then((r) => r.data);
export const listPortfolioFeatured = (params = {}) =>
  api.get("/portfolio/featured", { params }).then((r) => r.data);
export const listTestimonialsFeatured = () =>
  api.get("/testimonials/featured").then((r) => r.data);
export const listBanners = () => api.get("/banners").then((r) => r.data);
export const listFaqs = (params = {}) =>
  api.get("/faqs", { params }).then((r) => r.data);
export const listHelpArticles = () => api.get("/help").then((r) => r.data);
export const getPolicy = (slug) =>
  api.get(`/policies/${slug}`).then((r) => r.data);
export const getOccasionQuestionSet = (occasion) =>
  api.get(`/occasion-question-sets/${occasion}`).then((r) => r.data);
export const getPricingRules = () =>
  api.get("/pricing-rules").then((r) => r.data);
export const getPricingCalendar = (params = {}) =>
  api.get("/pricing-calendar", { params }).then((r) => r.data);

// ----- Phase 2 quote builder -----
export const upsertDraft = (draftId, payload) =>
  api
    .post("/quote-drafts", { draft_id: draftId || null, payload: payload || {} })
    .then((r) => r.data);
export const patchDraft = (draftId, payload) =>
  api
    .patch(`/quote-drafts/${draftId}`, { payload: payload || {} })
    .then((r) => r.data);
export const getDraft = (draftId) =>
  api.get(`/quote-drafts/${draftId}`).then((r) => r.data);

export const uploadReferenceImage = (draftId, file, onProgress) => {
  const form = new FormData();
  form.append("draft_id", draftId);
  form.append("file", file);
  return api
    .post("/quote-reference-images", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    })
    .then((r) => r.data);
};
export const deleteReferenceImage = (draftId, imageId) =>
  api
    .delete(`/quote-reference-images/${imageId}`, { params: { draft_id: draftId } })
    .then((r) => r.data);

export const toggleInspiration = (draftId, portfolioImageId) =>
  api
    .post("/quote-inspirations", {
      draft_id: draftId,
      portfolio_image_id: portfolioImageId,
    })
    .then((r) => r.data);

export const listPortfolioPaginated = (params = {}) =>
  api.get("/portfolio", { params }).then((r) => r.data);

export const trackEvent = (body) =>
  api.post("/events", body).then((r) => r.data);

// ----- Phase 3 buyer auth -----
export const startOtp = ({ phone, country_code = "+91" }) =>
  api.post("/auth/otp/start", { phone, country_code }).then((r) => r.data);
export const verifyOtp = ({ phone, code, country_code = "+91" }) =>
  api
    .post("/auth/otp/verify", { phone, code, country_code })
    .then((r) => r.data);
export const refreshAuth = (refresh_token) =>
  api.post("/auth/refresh", { refresh_token }).then((r) => r.data);
export const logoutAuth = (refresh_token) =>
  api.post("/auth/logout", { refresh_token }).then((r) => r.data);

// ----- Phase 3 quotes -----
export const submitQuote = (draft_id) =>
  api.post("/quotes", { draft_id }).then((r) => r.data);
export const listMyQuotes = () => api.get("/quotes").then((r) => r.data);
export const getMyQuote = (id) => api.get(`/quotes/${id}`).then((r) => r.data);

// ----- Phase 5 buyer actions on a received quote -----
export const acceptQuote = (id) =>
  api.post(`/quotes/${id}/accept`).then((r) => r.data);
export const declineQuote = (id, reason) =>
  api
    .post(`/quotes/${id}/decline`, { reason: reason || null })
    .then((r) => r.data);
export const askQuoteQuestion = (id, body) =>
  api.post(`/quotes/${id}/ask-question`, { body }).then((r) => r.data);

// ----- Phase 5 conversation REST fallback -----
export const listConversationMessages = (convId, { after } = {}) =>
  api
    .get(`/conversations/${convId}/messages`, {
      params: after ? { after } : {},
    })
    .then((r) => r.data);
export const postConversationMessage = (convId, body, attachments) =>
  api
    .post(`/conversations/${convId}/messages`, {
      body,
      attachments: attachments || [],
    })
    .then((r) => r.data);
export const uploadConversationAttachment = (convId, file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/conversations/${convId}/attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    })
    .then((r) => r.data);
};

// ----- Phase 6 buyer GDPR -----
export const exportQuoteData = (id) =>
  api.post(`/quotes/${id}/data-export`).then((r) => r.data);
export const deleteQuoteData = (id) =>
  api.post(`/quotes/${id}/delete-data`).then((r) => r.data);
